<script setup lang="ts" generic="T">
/**
 * Header row of one group in a grouped NxTable (`groupBy`): coloured dot,
 * title, count and a rule line across the row. A display row only — it is no
 * part of the table's rows, so navigation, selection and export never see it.
 * Internal to NxTable, which forwards its `#group-header` slot here.
 */
import { computed } from 'vue';
import { hueVars } from '../lib/hue';
import type { NxTableGroup, NxTableGroupHeaderSlotProps, NxTableGroupTone } from './internal';

const props = defineProps<{
  group: NxTableGroup;
  count: number;
  rows: T[];
  ungrouped: boolean;
  /** 1-based position of this row in the grid (`aria-rowindex`). */
  gridRowIndex: number;
  /** Columns the header cell spans (the grid's `aria-colcount`). */
  colCount: number;
  /** Accessible text of the count, e.g. „3 Einträge“. */
  countLabel: string;
}>();

defineSlots<{
  default?(props: NxTableGroupHeaderSlotProps<T>): unknown;
}>();

const TONE_DOT: Record<NxTableGroupTone, string> = {
  neutral: 'var(--nxt-fg-muted)',
  info: 'var(--nxt-info-bg)',
  success: 'var(--nxt-success-bg)',
  warning: 'var(--nxt-warning-bg)',
  danger: 'var(--nxt-danger-bg)',
};

// The dot colour rides one custom property: the intent tone when set, else the
// category hue, else the muted neutral dot.
const dotStyle = computed<Record<string, string>>(() => {
  if (props.group.tone) return { '--table-group-dot': TONE_DOT[props.group.tone] };
  if (props.group.hue) return hueVars(props.group.hue, '--table-group-dot');
  return { '--table-group-dot': TONE_DOT.neutral };
});
</script>

<template>
  <div
    class="tr group-row"
    role="row"
    :aria-rowindex="props.gridRowIndex"
    :data-group-key="props.group.key"
    :style="dotStyle"
  >
    <div class="group-cell" role="gridcell" aria-colindex="1" :aria-colspan="props.colCount">
      <div class="group-label">
        <slot
          :group="props.group"
          :count="props.count"
          :rows="props.rows"
          :ungrouped="props.ungrouped"
        >
          <span class="group-dot" aria-hidden="true"></span>
          <span class="group-title">{{ props.group.label }}</span>
          <span class="group-count" aria-hidden="true">{{ props.count }}</span>
          <span class="group-sr-only">{{ props.countLabel }}</span>
        </slot>
      </div>
      <span class="group-rule" aria-hidden="true"></span>
    </div>
  </div>
</template>

<style scoped>
.group-cell {
  display: flex;
  align-items: center;
  /* One cell across the whole row — the row is as wide as the widest data row. */
  flex: 1 0 auto;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-sm) var(--nxt-space-sm) var(--nxt-space-2xs);
  box-sizing: border-box;
}
/* The title stays in view while a wide grid scrolls sideways; its own fill
   covers the rule line it slides over. */
.group-label {
  position: sticky;
  left: 0;
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding-inline: var(--nxt-space-2xs) var(--nxt-space-xs);
  background-color: var(--row-bg, var(--nxt-table--bg));
  color: var(--nxt-table--header-fg);
  font-weight: var(--nxt-font-weight-semibold);
  white-space: nowrap;
}
.group-dot {
  flex-shrink: 0;
  width: var(--nxt-dot-size-md);
  height: var(--nxt-dot-size-md);
  border-radius: var(--nxt-radius-full);
  background: var(--table-group-dot);
}
.group-title {
  color: var(--nxt-table--header-fg);
}
.group-count {
  padding: 0 var(--nxt-space-xs);
  border: var(--nxt-hairline) solid var(--nxt-border-subtle);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-bg);
  color: var(--nxt-table--fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
  font-variant-numeric: tabular-nums;
}
.group-rule {
  flex: 1 1 auto;
  min-width: var(--nxt-space-md);
  border-top: var(--nxt-hairline) solid var(--nxt-table--border);
}
.group-sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
</style>
